import { BroadviewClientError, type DashboardResponse } from "@broadview/api-client";
import { PortalAccountMenu } from "./PortalAccountMenu";
import { Link } from "@tanstack/react-router";
import {
  Activity,
  ArrowRight,
  Boxes,
  CircleDot,
  Database,
  FileSearch,
  Gauge,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trophy,
  TrendingDown,
  X,
} from "lucide-react";
import { useState } from "react";
import { PortalDataError, PortalLoadingState } from "./PortalDataState";
import {
  parseGovernedMetricPayload,
  useDashboardQuery,
  useMetricQuery,
} from "@/lib/portal-queries";
import { usePortalRuntime } from "@/lib/portal-runtime";
import { buildDashboardViewModel } from "@/lib/dashboard-view-model";
import { formatMetricDisplayValue } from "@/lib/metric-display";

function freshnessLabel(asOf: string, freshness: string) {
  return `${freshness.replaceAll("_", " ")} · ${new Date(asOf).toLocaleString()}`;
}

export function LiveDashboard({
  embedded = false,
  showPreviewNotice = true,
}: Readonly<{ embedded?: boolean; showPreviewNotice?: boolean }>) {
  const { tenantContext } = usePortalRuntime();
  const dashboard = useDashboardQuery();
  const [selectedMetric, setSelectedMetric] = useState<string | null>(null);

  if (!tenantContext.permissions.includes("metric.read")) {
    const content = (
      <PortalDataError
        error={
          new BroadviewClientError({
            status: 403,
            code: "FORBIDDEN",
            message: "The selected Division does not grant metric.read.",
          })
        }
        onRetry={() => undefined}
        title="Dashboard access is not available"
      />
    );
    return embedded ? (
      content
    ) : (
      <DashboardShell divisionName={tenantContext.tenantName}>{content}</DashboardShell>
    );
  }

  const content = (
    <>
      {dashboard.isPending ? (
        <PortalLoadingState label="Loading live warranty dashboard" />
      ) : dashboard.isError ? (
        <PortalDataError error={dashboard.error} onRetry={() => void dashboard.refetch()} />
      ) : dashboard.data.widgets.length === 0 ? (
        <section className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
          <Database className="mx-auto h-7 w-7 text-muted-foreground" />
          <h2 className="mt-3 font-semibold">No governed KPI results are available</h2>
          <p className="mx-auto mt-1 max-w-xl text-sm text-muted-foreground">
            This Division is eligible, but no enabled metric has an approved result yet. No
            prototype values were substituted.
          </p>
        </section>
      ) : (
        <DashboardContent
          dashboard={dashboard.data}
          expectedTenantId={tenantContext.tenantId}
          stale={dashboard.isStale}
          updatedAt={dashboard.dataUpdatedAt}
          onSelectMetric={setSelectedMetric}
          onRefresh={() => void dashboard.refetch()}
          showPrimaryActions={!embedded}
          showEmbeddedPreviewNotice={embedded && showPreviewNotice}
        />
      )}
      {selectedMetric && dashboard.data && (
        <MetricEvidence
          dashboard={dashboard.data}
          definitionId={selectedMetric}
          onClose={() => setSelectedMetric(null)}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <section aria-label="Live governed warranty summary" className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs font-medium text-success">
            <CircleDot className="h-3 w-3" /> Governed API data
          </span>
          <span className="text-xs text-muted-foreground">{tenantContext.tenantName}</span>
        </div>
        {content}
      </section>
    );
  }

  return <DashboardShell divisionName={tenantContext.tenantName}>{content}</DashboardShell>;
}

function DashboardShell({
  divisionName,
  children,
}: {
  divisionName: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div
              className="flex h-9 w-9 items-center justify-center rounded-lg"
              style={{ background: "var(--gradient-primary)" }}
            >
              <ShieldCheck className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold">Broadview Warranty Intelligence</div>
              <div className="text-xs text-muted-foreground">{divisionName}</div>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs text-success md:inline-flex">
              <CircleDot className="h-3 w-3" /> Live governed data
            </span>
            <Link
              to="/top5"
              className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/15"
            >
              <Trophy className="h-3.5 w-3.5" /> Top 5
            </Link>
            <Link
              to="/problem-records"
              className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Problem Records
            </Link>
            <Link
              to="/effectiveness-prevention"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <Sparkles className="h-3.5 w-3.5" /> Prevention
            </Link>
            <Link
              to="/financial-intelligence"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <TrendingDown className="h-3.5 w-3.5" /> Financial
            </Link>
            <PortalAccountMenu />
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1500px] space-y-6 px-6 py-8">
        <section>
          <h1 className="text-2xl font-semibold tracking-tight">Executive Overview</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Governed warranty metrics for the selected Division.
          </p>
        </section>
        {children}
      </main>
    </div>
  );
}

function DashboardContent({
  dashboard,
  expectedTenantId,
  stale,
  updatedAt,
  onSelectMetric,
  onRefresh,
  showPrimaryActions,
  showEmbeddedPreviewNotice,
}: {
  dashboard: DashboardResponse;
  expectedTenantId: string;
  stale: boolean;
  updatedAt: number;
  onSelectMetric: (definitionId: string) => void;
  onRefresh: () => void;
  showPrimaryActions: boolean;
  showEmbeddedPreviewNotice: boolean;
}) {
  const icons = [Boxes, Gauge, Activity, Database];
  const viewModel = buildDashboardViewModel(
    dashboard,
    expectedTenantId,
    parseGovernedMetricPayload,
  );
  const hasDataWatermark = !dashboard.warnings.some(
    ({ code }) => code === "NO_GOVERNED_DATA_WATERMARK",
  );
  return (
    <>
      <section className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-xs">
        <span className={stale ? "text-warning" : "text-success"}>
          {stale ? "Refresh recommended" : "Query cache is fresh"}
        </span>
        <span className="text-muted-foreground">
          {hasDataWatermark
            ? `Data as of ${new Date(dashboard.dataAsOf).toLocaleString()}`
            : "No governed data watermark"}{" "}
          · retrieved {new Date(updatedAt).toLocaleTimeString()}
        </span>
        <button
          type="button"
          onClick={onRefresh}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 font-semibold hover:bg-muted"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </section>

      {dashboard.warnings.length > 0 && (
        <section className="rounded-xl border border-warning/40 bg-warning/10 p-4">
          <h2 className="text-sm font-semibold">Partial data</h2>
          <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
            {dashboard.warnings.map((warning) => (
              <li key={`${warning.code}-${warning.message}`}>{warning.message}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {viewModel.widgets.map((widget, index) => {
          const metric = widget.metric;
          const governedWidget = dashboard.widgets.find(
            (candidate) => candidate.widgetId === widget.widgetId,
          );
          const Icon = icons[index % icons.length]!;
          return (
            <button
              type="button"
              key={widget.widgetId}
              onClick={() => widget.definitionId && onSelectMetric(widget.definitionId)}
              disabled={!widget.definitionId}
              className="relative overflow-hidden rounded-xl border border-border p-5 text-left transition hover:border-primary/50"
              style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
            >
              <div className="flex items-start justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                  {widget.state.replaceAll("-", " ")}
                </span>
              </div>
              <div className="mt-4 text-xs text-muted-foreground">{widget.label}</div>
              <div className="mt-1 text-2xl font-semibold">
                {widget.displayValue ?? widget.stateDetail}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                <span className="rounded-full bg-muted px-2 py-0.5 font-semibold text-foreground">
                  {governedWidget?.dataState ?? "UNAVAILABLE"}
                </span>
                {governedWidget?.authorityClass && (
                  <span className="rounded-full bg-warning/10 px-2 py-0.5 font-semibold text-warning">
                    {governedWidget.authorityClass}
                  </span>
                )}
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                {widget.asOf
                  ? freshnessLabel(widget.asOf, metric?.freshness ?? "UNKNOWN")
                  : "No governed result timestamp"}
              </div>
            </button>
          );
        })}
        {showEmbeddedPreviewNotice && (
          <aside
            role="note"
            className="rounded-xl border border-amber-300/70 bg-amber-950/90 p-4 text-sm leading-5 text-amber-50 shadow-[0_12px_30px_rgba(0,0,0,0.2)] sm:col-span-2 lg:col-span-2"
          >
            <strong className="text-amber-100">Preview modules</strong>
            <p className="mt-1 text-xs leading-5 text-amber-50/90">
              Demonstration-only sections below are not substituted for the governed summary and do
              not trigger backend mutations.
            </p>
          </aside>
        )}
      </section>

      {showPrimaryActions && (
        <section
          className="rounded-xl border border-primary/30 p-5"
          style={{ background: "var(--gradient-card)", boxShadow: "var(--shadow-card)" }}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Trophy className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-base font-semibold">Top 5 Intelligence</h2>
                <p className="text-xs text-muted-foreground">
                  Governed ranked issues, claim evidence, and source provenance are available.
                </p>
              </div>
            </div>
            <Link
              to="/top5"
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-4 py-2.5 text-xs font-semibold lg:ml-auto"
            >
              Open Top 5 <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              to="/problem-records"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground"
            >
              Open Problem Records
            </Link>
          </div>
        </section>
      )}
    </>
  );
}

function MetricEvidence({
  dashboard,
  definitionId,
  onClose,
}: {
  dashboard: DashboardResponse;
  definitionId: string;
  onClose: () => void;
}) {
  const metric = useMetricQuery(definitionId, dashboard);
  const widget = dashboard.widgets.find(
    (candidate) =>
      candidate.widgetId === definitionId ||
      parseGovernedMetricPayload(candidate.payload)?.definitionId === definitionId,
  );
  const hasEvidenceTimestamp = !dashboard.warnings.some(
    ({ code, widgetIds }) =>
      code === "METRIC_TIMESTAMP_UNAVAILABLE" &&
      widget !== undefined &&
      widgetIds?.includes(widget.widgetId),
  );
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-background/70 backdrop-blur-sm">
      <button
        type="button"
        className="flex-1"
        aria-label="Close metric evidence"
        onClick={onClose}
      />
      <aside className="h-full w-full max-w-xl overflow-y-auto border-l border-border bg-card p-6">
        <div className="flex items-start gap-3">
          <FileSearch className="h-5 w-5 text-primary" />
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              KPI evidence
            </div>
            <h2 className="mt-1 text-lg font-semibold">{metric.data?.label ?? definitionId}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-md border border-border p-1.5"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {metric.isPending ? (
          <div className="mt-6 h-28 animate-pulse rounded-lg bg-muted" />
        ) : metric.isError ? (
          <div className="mt-6">
            <PortalDataError
              error={metric.error}
              onRetry={() => void metric.refetch()}
              title="Metric evidence could not be loaded"
            />
          </div>
        ) : (
          <dl className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <Evidence label="Value" value={formatMetricDisplayValue(metric.data)} />
            <Evidence
              label="Definition"
              value={`${metric.data.definitionId} v${metric.data.definitionVersion}`}
            />
            <Evidence label="Freshness" value={metric.data.freshness} />
            <Evidence label="Quality" value={metric.data.quality} />
            <Evidence label="Reconciliation" value={metric.data.reconciliation} />
            <Evidence label="Data state" value={metric.data.dataState} />
            <Evidence label="Authority" value={widget?.authorityClass ?? "Not supplied"} />
            <Evidence
              label="As of"
              value={
                hasEvidenceTimestamp
                  ? new Date(metric.data.asOf).toLocaleString()
                  : "No governed result timestamp"
              }
            />
          </dl>
        )}
        <section className="mt-6 rounded-lg border border-border bg-background p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Source and provenance
          </div>
          <p className="mt-2 text-sm">
            {widget?.sourceSummary ?? metric.data?.sourceSummary ?? "No source summary supplied."}
          </p>
          {widget?.authorityClass && (
            <p className="mt-2 text-xs text-muted-foreground">Authority: {widget.authorityClass}</p>
          )}
        </section>
      </aside>
    </div>
  );
}

function Evidence({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 font-semibold">{value}</dd>
    </div>
  );
}
